import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxDateBoxModule, DxSelectBoxModule, DxTextAreaModule, DxTextBoxModule, DxValidatorModule } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { ConfirmDeactivationCommand, ConfirmTerminationCommand, DelimitPositionCommand, EmployeesClient, IUserStatusUpdateCommand, NestleDeactivationGridViewDto } from '@services';
import { Observable } from 'rxjs';
import { FileUploadComponent } from 'src/app/components';
import { FormPopupComponent } from 'src/app/components/utils/form-popup/form-popup.component';
import { SignedLinkDirective } from 'src/app/directives/signed-link.directive';
import { DATE_FORMAT, MAX_FILE_SIZE, YES_NO_OPTIONS } from 'src/app/shared/constants';
import { FileUtils } from 'src/app/shared/file-utils';

export type UserStatusUpdateMode = 'confirmTermination' | 'confirmDeactivation' | 'delimitPosition';

@Component({
    selector: 'user-status-update-popup',
    templateUrl: './user-status-update-popup.component.html',
    imports: [
        CommonModule,
        DxDateBoxModule,
        DxSelectBoxModule,
        DxTextAreaModule,
        DxTextBoxModule,
        DxValidatorModule,
        FileUploadComponent,
        FormPopupComponent,
        SignedLinkDirective
    ]
})
export class UserStatusUpdatePopupComponent {
    @Input() userId: string;
    @Input() mode: UserStatusUpdateMode = 'confirmTermination';
    // A saved record opens the popup read-only.
    @Input() record: NestleDeactivationGridViewDto | null = null;
    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() confirmed = new EventEmitter<void>();

    private employeesClient = inject(EmployeesClient);

    protected effectiveDate: Date;
    protected positionDelimited: boolean;
    // Initialised so the first reset() isn't a value change, which would show the required error on open.
    protected ticketNumber = '';
    protected note = '';
    protected files: File[] = [];
    protected isSaving = false;

    protected readonly dateFormat = DATE_FORMAT;
    protected readonly yesNoOptions = YES_NO_OPTIONS;
    // The required rule would reject No (false).
    protected readonly isSelected = (e: { value: unknown }): boolean => e.value !== null && e.value !== undefined;

    protected get isReadOnly(): boolean {
        return !!this.record;
    }

    protected reset() {
        this.effectiveDate = this.record?.effectiveDate ?? new Date(new Date().setHours(0, 0, 0, 0));
        this.positionDelimited = this.record?.positionDelimited ?? this.mode === 'delimitPosition';
        this.ticketNumber = this.record?.ticketNumber ?? '';
        this.note = this.record?.note ?? '';
        this.files = [];
        this.isSaving = false;
    }

    protected onVisibleChange(visible: boolean) {
        this.visible = visible;
        this.visibleChange.emit(visible);
    }

    protected async confirm() {
        if (this.files.some(file => file.size > MAX_FILE_SIZE)) {
            this.notifyError('Each file must be less than 15 MB in size.');
            return;
        }

        this.isSaving = true;
        const data = {
            userId: this.userId,
            effectiveDate: this.effectiveDate,
            positionDelimited: this.positionDelimited,
            ticketNumber: this.ticketNumber,
            note: this.note || undefined,
            uploadedFiles: await FileUtils.mapFilesToDto(this.files)
        };
        this.send(data).subscribe({
            next: () => {
                notify({message: this.successMessage, position: 'bottom center'}, 'success', 3000);
                this.onVisibleChange(false);
                this.confirmed.emit();
            },
            error: (errors: unknown) => {
                this.isSaving = false;
                if (Array.isArray(errors))
                    errors.forEach(error => this.notifyError(error.message));
                else
                    this.notifyError('Failed to update the user status');
            }
        });
    }

    private send(data: IUserStatusUpdateCommand): Observable<void> {
        switch (this.mode) {
            case 'confirmTermination': return this.employeesClient.confirmTermination(new ConfirmTerminationCommand(data));
            case 'confirmDeactivation': return this.employeesClient.confirmDeactivation(new ConfirmDeactivationCommand(data));
            case 'delimitPosition': return this.employeesClient.delimitPosition(new DelimitPositionCommand(data));
        }
    }

    private get successMessage(): string {
        switch (this.mode) {
            case 'confirmTermination': return 'Termination confirmed';
            case 'confirmDeactivation': return 'Deactivation confirmed';
            case 'delimitPosition': return this.positionDelimited ? 'Position delimited' : 'No changes were recorded';
        }
    }

    private notifyError(message: string) {
        notify({
            message,
            type: 'error',
            displayTime: 5000,
        }, {
            position: 'bottom right',
            direction: 'up-push'
        });
    }
}
